import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { LogOut, Mail, Phone, Calendar, User, MessageSquare, Copy, Check, Settings, Lock, Edit, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const AdminDashboard = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, processed
  const [copiedId, setCopiedId] = useState(null);
  const [editingNotes, setEditingNotes] = useState({});
  const [notesText, setNotesText] = useState({});
  const [expandedCards, setExpandedCards] = useState({});
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Anfragen');
      }

      const data = await response.json();
      setInquiries(data.inquiries || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Anfragen');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
    toast.success('Erfolgreich ausgeloggt');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Neue Passwörter stimmen nicht überein');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/change-password`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            current_password: passwordData.currentPassword,
            new_password: passwordData.newPassword
          }),
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler beim Ändern des Passworts');
      }
      
      toast.success('Passwort erfolgreich geändert!');
      setShowPasswordDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.message);
      console.error('Error:', error);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) throw new Error('Fehler beim Aktualisieren');

      toast.success('Status aktualisiert');
      fetchInquiries();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Status');
      console.error('Error:', error);
    }
  };

  const updateNotes = async (id, notes) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ notes }),
        }
      );

      if (!response.ok) throw new Error('Fehler beim Speichern');

      toast.success('Notizen gespeichert');
      setEditingNotes(prev => ({ ...prev, [id]: false }));
      fetchInquiries();
    } catch (error) {
      toast.error('Fehler beim Speichern der Notizen');
      console.error('Error:', error);
    }
  };

  const toggleEditNotes = (id, currentNotes) => {
    if (editingNotes[id]) {
      // Speichern
      updateNotes(id, notesText[id] || '');
    } else {
      // Bearbeitungsmodus aktivieren
      setNotesText(prev => ({ ...prev, [id]: currentNotes || '' }));
      setEditingNotes(prev => ({ ...prev, [id]: true }));
    }
  };

  const toggleCard = (id) => {
    setExpandedCards(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Möchten Sie diese Anfrage wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Fehler beim Löschen');

      toast.success('Anfrage gelöscht');
      fetchInquiries();
    } catch (error) {
      toast.error('Fehler beim Löschen der Anfrage');
      console.error('Error:', error);
    }
  };

  const copyEmail = (email, id) => {
    navigator.clipboard.writeText(email);
    setCopiedId(id);
    toast.success('E-Mail-Adresse kopiert!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredInquiries = inquiries.filter((inquiry) => {
    if (filter === 'all') return true;
    return inquiry.status === filter;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A961] mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Anfragen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0A2540] to-[#1E3A5F] text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-sm text-gray-300 mt-1">Seeschloss-Hotel Kellersee</p>
            </div>
            <div className="flex items-center space-x-3">
              <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Passwort ändern
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Admin-Passwort ändern</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Aktuelles Passwort
                      </label>
                      <input
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#C9A961]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Neues Passwort
                      </label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#C9A961]"
                        required
                        minLength={8}
                      />
                      <p className="text-xs text-gray-500 mt-1">Mindestens 8 Zeichen</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Neues Passwort bestätigen
                      </label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#C9A961]"
                        required
                      />
                    </div>
                    <div className="flex justify-end space-x-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowPasswordDialog(false)}
                      >
                        Abbrechen
                      </Button>
                      <Button
                        type="submit"
                        className="bg-gradient-to-r from-[#C9A961] to-[#D4AF37]"
                      >
                        Passwort ändern
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Gesamt</p>
                  <p className="text-3xl font-bold text-gray-900">{inquiries.length}</p>
                </div>
                <Mail className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Neu</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {inquiries.filter((i) => i.status === 'new').length}
                  </p>
                </div>
                <MessageSquare className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-gray-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Bearbeitet</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {inquiries.filter((i) => i.status === 'processed').length}
                  </p>
                </div>
                <Check className="w-10 h-10 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-semibold text-gray-700">Filter:</span>
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-[#C9A961]' : ''}
            >
              Alle
            </Button>
            <Button
              variant={filter === 'new' ? 'default' : 'outline'}
              onClick={() => setFilter('new')}
              className={filter === 'new' ? 'bg-green-600' : ''}
            >
              Neu
            </Button>
            <Button
              variant={filter === 'processed' ? 'default' : 'outline'}
              onClick={() => setFilter('processed')}
              className={filter === 'processed' ? 'bg-gray-600' : ''}
            >
              Bearbeitet
            </Button>
          </div>
        </div>

        {/* Inquiries List */}
        <div className="space-y-4">
          {filteredInquiries.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Keine Anfragen gefunden</p>
              </CardContent>
            </Card>
          ) : (
            filteredInquiries.map((inquiry) => (
              <Card key={inquiry.id} className="hover:shadow-lg transition-shadow">
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleCard(inquiry.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <User className="w-5 h-5 text-[#C9A961]" />
                      <div className="flex-1">
                        <CardTitle className="text-lg">{inquiry.name}</CardTitle>
                        <p className="text-sm text-gray-600 mt-1">{inquiry.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {inquiry.status === 'new' ? (
                        <Badge className="bg-blue-600">Neu</Badge>
                      ) : (
                        <Badge variant="secondary">Bearbeitet</Badge>
                      )}
                      <div className="text-sm text-gray-500">
                        {new Date(inquiry.created_at).toLocaleDateString('de-DE')}
                      </div>
                      {expandedCards[inquiry.id] ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                {expandedCards[inquiry.id] && (
                  <CardContent className="p-6 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-[#C9A961]" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">E-Mail</p>
                          <div className="flex items-center space-x-2">
                            <p className="font-medium">{inquiry.email}</p>
                            <button
                              onClick={() => copyEmail(inquiry.email, inquiry.id)}
                              className="text-[#C9A961] hover:text-[#D4AF37] transition-colors"
                            >
                              {copiedId === inquiry.id ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-[#C9A961]" />
                        <div>
                          <p className="text-sm text-gray-600">Telefon</p>
                          <p className="font-medium">{inquiry.phone || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Zusätzliche Felder */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 bg-gray-50 p-4 rounded-lg border">
                      {inquiry.company && (
                        <div>
                          <p className="text-sm text-gray-600 font-semibold">Unternehmen</p>
                          <p className="font-medium text-gray-800">{inquiry.company}</p>
                        </div>
                      )}
                      {inquiry.investmentType && (
                        <div>
                          <p className="text-sm text-gray-600 font-semibold">Investitionsart</p>
                          <p className="font-medium text-gray-800">{inquiry.investmentType}</p>
                        </div>
                      )}
                      {inquiry.investmentVolume && (
                        <div>
                          <p className="text-sm text-gray-600 font-semibold">Investitionsvolumen</p>
                          <p className="font-medium text-gray-800">{inquiry.investmentVolume}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Anfragen/Checkboxen */}
                    {(inquiry.requestExpose || inquiry.requestViewing || inquiry.hasBonityProof) && (
                      <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Anfragen & Nachweise:</p>
                        <div className="space-y-2">
                          {inquiry.requestExpose && (
                            <div className="flex items-center space-x-2">
                              <Check className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-700">Vollständiges Exposé per E-Mail angefordert</span>
                            </div>
                          )}
                          {inquiry.requestViewing && (
                            <div className="flex items-center space-x-2">
                              <Check className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-700">Besichtigungstermin gewünscht</span>
                            </div>
                          )}
                          {inquiry.hasBonityProof && (
                            <div className="flex items-center space-x-2">
                              <Check className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-700">Bonitätsnachweis kann erbracht werden</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {inquiry.message && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 font-semibold mb-2">Nachricht:</p>
                        <p className="text-gray-800 bg-gray-50 p-4 rounded-lg border">
                          {inquiry.message}
                        </p>
                      </div>
                    )}
                    
                    {/* Admin Notizen */}
                    <div className="mb-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-bold text-gray-700">📝 Admin-Notizen:</p>
                        <Button
                          onClick={() => toggleEditNotes(inquiry.id, inquiry.notes)}
                          variant="outline"
                          size="sm"
                          className="h-8"
                        >
                          {editingNotes[inquiry.id] ? (
                            <>
                              <Save className="w-4 h-4 mr-1" />
                              Speichern
                            </>
                          ) : (
                            <>
                              <Edit className="w-4 h-4 mr-1" />
                              Bearbeiten
                            </>
                          )}
                        </Button>
                      </div>
                      {editingNotes[inquiry.id] ? (
                        <textarea
                          value={notesText[inquiry.id] || ''}
                          onChange={(e) => setNotesText(prev => ({ ...prev, [inquiry.id]: e.target.value }))}
                          className="w-full min-h-[120px] p-3 border-2 border-yellow-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-[#C9A961] resize-y"
                          placeholder="Notizen hinzufügen... z.B. Exposé gesendet, Besichtigung vereinbart, etc."
                        />
                      ) : (
                        <div className="text-gray-700 whitespace-pre-wrap">
                          {inquiry.notes || <span className="text-gray-400 italic">Keine Notizen vorhanden. Klicken Sie auf "Bearbeiten" um Notizen hinzuzufügen.</span>}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {inquiry.status === 'new' ? (
                        <Button
                          onClick={() => updateStatus(inquiry.id, 'processed')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Als bearbeitet markieren
                        </Button>
                      ) : (
                        <Button
                          onClick={() => updateStatus(inquiry.id, 'new')}
                          variant="outline"
                        >
                          Als neu markieren
                        </Button>
                      )}
                      <Button
                        onClick={() => deleteInquiry(inquiry.id)}
                        variant="destructive"
                      >
                        Löschen
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
